import React, { useEffect, useRef } from "react";
import { Box, Container, Skeleton, Stack, Typography } from "@mui/material";
import PropertySlider from "../../../../Components/PropertySlider/PropertySlider";
import PropertyCard from "../../../../Components/PropertyCard/PropertyCard";
import { useNavigate } from "react-router-dom";
import { RightPaddingWrapper } from "../../../../Components/RightPaddingWrapper/RightPaddingWrapper";
import { useLazyGetPropertyBasedOnLocationQuery } from "../../../../Services/auto-complete.services";
import { useSelector } from "react-redux";

// New York City coordinates (Manhattan) - fallback location
const NEW_YORK_COORDS = {
  lat: 40.7128,
  lng: -74.006,
};

// Approximate US continental boundaries
const US_BOUNDS = {
  north: 49.384358,
  south: 24.396308,
  east: -66.93457,
  west: -125.0,
};

function isLocationInUS(lat: number, lng: number): boolean {
  return (
    lat >= US_BOUNDS.south &&
    lat <= US_BOUNDS.north &&
    lng >= US_BOUNDS.west &&
    lng <= US_BOUNDS.east
  );
}

type LocationBasedPropertyProps = {
  headerText?: "Browse homes in";
  locationText?: any;
};

export function getCurrentLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      // Geolocation not supported - fallback to New York
      resolve(NEW_YORK_COORDS);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Check if location is within US boundaries
        if (isLocationInUS(lat, lng)) {
          resolve({ lat, lng });
        } else {
          // Location outside US - fallback to New York
          resolve(NEW_YORK_COORDS);
        }
      },
      (error) => {
        // Log the actual error for debugging
        console.warn("Geolocation error:", error.code, error.message);
        // Permission denied or error - fallback to New York
        resolve(NEW_YORK_COORDS);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  });
}

const LocationBasedProperty = () => {
  const [trigger] = useLazyGetPropertyBasedOnLocationQuery(
    {}
  );
  const [cachedData, setCachedData] = React.useState<any>(
    useSelector((state: any) => state?.propertyReducer?.locationBased) || null
  );
  const hasFetchedRef = useRef(false);

  const __ss =
    useSelector((state: any) => state?.propertyReducer?.locationBased) || null;

  const navigate = useNavigate();
  const displayData = __ss?.data || cachedData?.data;

  useEffect(() => {
    // Prevent duplicate fetches using ref
    if (hasFetchedRef.current) return;
    if (displayData != null && Object.keys(displayData).length > 0) return;

    hasFetchedRef.current = true;

    const fetchData = async () => {
      getCurrentLocation()
        .then(({ lat, lng }) => {
          return trigger({ lat, long: lng }).unwrap();
        })
        .then((response: any) => {
          if (response) {
            setCachedData(response);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch location-based properties:", err);
          // Don't attempt a second trigger if the first one failed
        });
    };
    fetchData();
  }, [displayData, trigger]);

  if (displayData == null || Object.keys(displayData).length === 0) {
    return (
      <Container maxWidth={"xl"} disableGutters>
        <RightPaddingWrapper>
          <Stack direction={"column"} spacing={"30px"} pt={"20px"}>
            <Box>
              <Skeleton animation="wave" variant="text" width={300} height={40} />
            </Box>
            <Stack direction={"row"} gap={"10px"} sx={{ overflow: "hidden" }}>
              {Array.from(new Array(4)).map((v: any, i: any) => (
                <Box
                  key={i}
                  sx={{
                    minWidth: { xs: "280px", sm: "300px", md: "320px" },
                    borderRadius: "15px",
                    overflow: "hidden",
                  }}
                >
                  <Stack direction={"column"} height={"100%"} overflow={"hidden"}>
                    <Skeleton
                      animation="wave"
                      variant="rectangular"
                      sx={{
                        width: "100%",
                        height: { xs: "240px", md: "250px", lg: "260px", xl: "275px" },
                        borderRadius: "15px 15px 0 0",
                      }}
                    />
                    <Box p={"25px"}>
                      <Stack direction={"column"} spacing={1}>
                        <Skeleton animation="wave" width="80%" height={28} />
                        <Skeleton animation="wave" width="60%" height={24} />
                        <Skeleton animation="wave" width="70%" height={20} />
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Stack>
        </RightPaddingWrapper>
      </Container>
    );
  }

  return (
    <Container maxWidth={"xl"} disableGutters>
      <RightPaddingWrapper>
        <Stack direction={"column"} spacing={"30px"} pt={"20px"}>
          <Box>
            <Typography variant="h4">
              {`Browse homes in  ${displayData?.stateDetails?.stateName}`}
              <Typography
                pl={2}
                fontWeight={"500"}
                variant="h4"
                component={"span"}
              >
                {displayData?.stateDetails?.stateID}
              </Typography>
            </Typography>
          </Box>
          <Box>
            <PropertySlider
              setting={{
                slidesToScroll: 1,
                autoplay: displayData?.result > 4 ? true : false,
                autoplaySpeed: displayData?.result > 2000 ? true : false,
              }}
            >
              {displayData?.results?.map((v: any, i: any) => (
                <PropertyCard
                  key={i}
                  id={v?.propertyRefID}
                  propertyId={v?.propertyId}
                  type="product"
                  isClaimed={v?.isClaimed}
                  featuredPhoto={v?.featuredPhoto}
                  mls={v?.mls}
                  propertyName={v?.address?.address}
                  bathCount={v?.propertyOverView?.bathrooms || "0"}
                  bedCount={v?.propertyOverView?.bedrooms}
                  stateName={v?.address?.state}
                  stateZip={v?.address?.zip}
                  sqrFeet={v?.propertyOverView?.lotSize}
                  // stateId={v?.mailAddress?.stateDetails?.stateID}
                  href={`/${v?.propertyId}/property-id/${v?.propertyId}`}
                  city={v?.address?.city}
                />
              ))}
            </PropertySlider>
          </Box>
        </Stack>
      </RightPaddingWrapper>
    </Container>
  );
};

export default LocationBasedProperty;
